import React, { useState, useEffect, useCallback } from "react";
import { SpeedometerDashboard } from "./SpeedoMeter";
import {
  getSpecificationEditApi,
  submitSpecificationAPI,
} from "./table-list-api.js";
import edit from "../../../../assets/Vehicles/edit.png";
import weight from "../../../../assets/Vehicles/weight.png";
import performance from "../../../../assets/Vehicles/performance.png";
import fuel from "../../../../assets/Vehicles/fuel.png";
import transmission from "../../../../assets/Vehicles/transmission.png";
import engine from "../../../../assets/Vehicles/engine.png";
import DOMPurify from "dompurify";

const createConfig = (label, readKey, writeKey = readKey, extra = {}) => ({
  label,
  readKey,
  writeKey,
  ...extra,
});

// Helper to init state
const getInitialState = (config) => config.map((c) => ({ ...c, value: "" }));

const VEHICLE_CONFIG = [
  createConfig("Width", "dimension_width"),
  createConfig("Height", "dimension_height"),
  createConfig("Length", "dimension_length"),
  createConfig("Interior value", "interio_value", "interior_value"),
  createConfig("Large value", "large_value"),
  createConfig("Passenger value", "passenger_value"),
  createConfig("Bed length", "bed_length"),
  createConfig("Ground clearance", "ground_clearence"),
];

const WEIGHT_CONFIG = [
  createConfig("Kerb weight", "kerb_weight"),
  createConfig("Gross weight", "gross_weight"),
];

const PERFORMANCE_CONFIG = [
  createConfig("Touring cap.", "touring_capacity"),
  createConfig("Maximum payload", "maximum_payload"),
];

const FUEL_CONFIG = [
  createConfig("Type", "fuel_type"),
  createConfig("Quantity", "fuel_quantity"),
];

const TRANSMISSION_CONFIG = [
  createConfig("Type", "transmission_type"),
  createConfig("Gears", "transmission_gears"),
];

const ENGINE_CONFIG = [
  createConfig("Summary", "engine_summary", "engine_summary", { image: engine }),
  createConfig("Block Type", "block_type"),
  createConfig("Cam Type", "cam_type"),
  createConfig("Compression", "compression"),
  createConfig("Cylinders", "cylinders"),
  createConfig("Displacement", "displacement"),
  createConfig("Fuel Indication", "fuel_indication"),
  createConfig("Horse Power", "horse_power"),
  createConfig("Maximum Torque", "maximum_torque"),
  createConfig("Red Line RPM", "red_line_rpm"),
  createConfig("Stroke", "stroke"),
  createConfig("Valves", "valves"),
];

const WHEEL_CONFIG = [
  createConfig("Brake System", "brake_system"),
  createConfig("No. of Tires", "no_of_tires"),
  createConfig("Front Tire Size", "front_tire_size"),
  createConfig("Back Tire Size", "back_tire_size"),
  createConfig("Spare Size", "spare_size"),
  createConfig("Front Tire Air Pressure (Front)", "front_tire_air_pressure"),
  createConfig("Back Tire Air Pressure (Rear)", "back_tire_air_pressure"),
];

export const Specifications = ({ vehicleId }) => {
  const [specificationDetail, setSpecificationDetail] = useState({});
  const [fuelEconomy, setFuelEconomy] = useState({});
  const [loading, setLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  // States
  const [vehicleData, setVehicleData] = useState(() => getInitialState(VEHICLE_CONFIG));
  const [weightState, setWeightState] = useState(() => getInitialState(WEIGHT_CONFIG));
  const [performanceState, setPerformanceState] = useState(() => getInitialState(PERFORMANCE_CONFIG));
  const [fuelState, setFuelState] = useState(() => getInitialState(FUEL_CONFIG));
  const [transmissionState, setTransmissionState] = useState(() => getInitialState(TRANSMISSION_CONFIG));
  const [engineData, setEngineData] = useState(() => getInitialState(ENGINE_CONFIG));
  const [wheelData, setWheelData] = useState(() => getInitialState(WHEEL_CONFIG));

  const getSpecificationEdit = useCallback(async () => {
    if (!vehicleId) return;
    try {
      const response = await getSpecificationEditApi(`specification/edit/${vehicleId}`);
      if (response.data.success === true) {
        setSpecificationDetail(response.data.specification ?? {});
        setFuelEconomy(response.data.fuel_economy ?? {});
      }
      return response.data;
    } catch (err) {
      console.error("Error fetching category:", err);
    }
  }, [vehicleId]);

  useEffect(() => {
    getSpecificationEdit();
  }, [getSpecificationEdit]);

  // Sync state with API data
  useEffect(() => {
    const syncState = (config) =>
      config.map((c) => ({
        ...c,
        value: specificationDetail[c.readKey] ?? "",
      }));

    setVehicleData(syncState(VEHICLE_CONFIG));
    setWeightState(syncState(WEIGHT_CONFIG));
    setPerformanceState(syncState(PERFORMANCE_CONFIG));
    setFuelState(syncState(FUEL_CONFIG));
    setTransmissionState(syncState(TRANSMISSION_CONFIG));
    setEngineData(syncState(ENGINE_CONFIG));
    setWheelData(syncState(WHEEL_CONFIG));
  }, [specificationDetail]);

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const preSanitizeData = { vehicle_id: vehicleId };

      const collectData = (stateData) => {
        stateData.forEach((item) => {
          if (item.writeKey) {
            preSanitizeData[item.writeKey] = item.value;
          }
        });
      };

      collectData(vehicleData);
      collectData(weightState);
      collectData(performanceState);
      collectData(fuelState);
      collectData(transmissionState);
      collectData(engineData);
      collectData(wheelData);

      const sanitizedData = {};
      Object.entries(preSanitizeData).forEach(([key, value]) => {
        if (typeof value === "boolean" || typeof value === "number" || Array.isArray(value)) {
          sanitizedData[key] = value;
        } else {
          sanitizedData[key] = DOMPurify.sanitize(value);
        }
      });

      let response;
      if (vehicleId !== null) {
        response = await submitSpecificationAPI(sanitizedData);
      }

      if (response && response.data && response.data.success === true) {
        getSpecificationEdit();
        setIsEditing(false);
      }
    } catch (err) {
      console.error("Error submitting form:", err);
    } finally {
      setLoading(false);
    }
  };

  // Helper render for tables to reduce duplication
  const renderTable = (data) => (
    <div className="table-container rounded-4 overflow-hidden my-3">
      <table className="table table-borderless mb-0">
        <tbody>
          {data.map((item, index) => (
            <tr key={index}>
              <td className="px-4 py-3 fs-13 fw-500 w-25" style={{ backgroundColor: "#F6F6F8" }}>
                {item.label}
              </td>
              <td className="px-4 py-3 fs-13 fw-400 font-semibold">
                {item.image ? (
                  <>
                    <img src={item.image} alt="icon" style={{ width: "25px", marginRight: "8px" }} />
                    {item.value}
                  </>
                ) : (
                  item.value
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  // Helper render for input fields
  const renderInputs = (data, setData) => (
    <div className="row">
      {data.map((item, index) => (
        <div key={index} className="col-md-6 mb-3">
          <label className="d-block mb-1">{item.label}</label>
          <input
            type="text"
            className="form-control"
            value={item.value}
            onChange={(e) => {
              const updated = [...data];
              updated[index] = { ...updated[index], value: e.target.value };
              setData(updated);
            }}
          />
        </div>
      ))}
    </div>
  );

  // Helper render for simple list items in the 4-quadrant section
  const renderSimpleList = (title, icon, color, bgColor, data, setData) => (
    <div className="bg-white p-3 rounded-4 h-100">
      {isEditing ? (
        <>
          <p className="mb-1">{title}</p>
          {data.map((item, index) => (
            <div key={index} className="mb-3">
              <label className="d-block mb-1">{item.label}</label>
              <input
                type="text"
                className="form-control"
                value={item.value}
                onChange={(e) => {
                  const updated = [...data];
                  updated[index] = { ...updated[index], value: e.target.value };
                  setData(updated);
                }}
              />
            </div>
          ))}
        </>
      ) : (
        <>
          <div className="d-flex gap-3 mb-3 align-items-center">
            <img src={icon} alt={title} />
            <p className="m-0 fs-14 fw-600" style={{ color: color }}>
              {title}
            </p>
          </div>
          {data.map((item, index) => (
            <div key={index} className="rounded-4 p-3 mb-3" style={{ backgroundColor: bgColor }}>
              <p className="mb-1" style={{ color: "#8790A1" }}>
                {item.label}
              </p>
              <p className="m-0">{item.value}</p>
            </div>
          ))}
        </>
      )}
    </div>
  );

  return (
    <>
      <div className="col-12">
        {fuelEconomy.hidden === false && (
          <div className="bg-white rounded-4 p-3 map-card mb-3">
            <p>Fuel economy</p>
            <div className="">{<SpeedometerDashboard />}</div>
          </div>
        )}
      </div>
      <div className="col-xl-6 col-12 mb-3">
        <div className="bg-white rounded-4 p-3 h-100">
          <div className="d-flex justify-content-between align-items-center">
            <div className="d-flex flex-column">
              <p className="fs-16 fw-600" style={{ color: "#21272C" }}>
                Vehicle Information
              </p>
              <p className="fs-14 fw-400" style={{ color: "#8790A1" }}>
                You can also edit these entire details
              </p>
            </div>
            <button
              className="border-0 px-4 py-2 text-dark-purple rounded-pill"
              style={{ backgroundColor: "#F5E6FF" }}
              onClick={() => {
                if (isEditing) {
                  handleSubmit();
                } else {
                  setIsEditing(!isEditing);
                }
              }}
              disabled={loading}
            >
              {isEditing ? (
                loading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    Saving...
                  </>
                ) : (
                  "Save"
                )
              ) : (
                <>
                  <img src={edit} alt="edit" /> Edit
                </>
              )}
            </button>
          </div>
          {isEditing ? renderInputs(vehicleData, setVehicleData) : renderTable(vehicleData)}
        </div>
      </div>

      <div className="col">
        <div className="row mb-3">
          <div className="col-xl-6 mb-3 mb-xl-0">
            {renderSimpleList("Weight", weight, "#00AC56", "#E8FFF3", weightState, setWeightState)}
          </div>
          <div className="col-xl-6">
            {renderSimpleList("Performance", performance, "#529EF2", "#EBF5FF", performanceState, setPerformanceState)}
          </div>
        </div>
        <div className="row mb-3">
          <div className="col-xl-6 mb-3 mb-xl-0">
            {renderSimpleList("Fuel", fuel, "#FBC842", "#FFF9E8", fuelState, setFuelState)}
          </div>
          <div className="col-xl-6">
            {renderSimpleList("Transmission", transmission, "#ff7fa5ff", "#FFEEF3", transmissionState, setTransmissionState)}
          </div>
        </div>
      </div>

      <div className="col-xl-6 col-12 mb-3">
        <div className="bg-white rounded-4 p-3 h-100">
          <p className="fs-16 fw-600">Engine Information</p>
          {isEditing ? renderInputs(engineData, setEngineData) : renderTable(engineData)}
        </div>
      </div>
      <div className="col">
        <div className="bg-white rounded-4 p-3 h-100">
          <p className="fs-16 fw-600">Wheel and Tires</p>
          {isEditing ? renderInputs(wheelData, setWheelData) : renderTable(wheelData)}
        </div>
      </div>
    </>
  );
};
